// @rpg-unit/accessibility-options: the accessibility options applied across the game (tier 4; composition framework,
// decision 0077). Cut from spike 002's `src/a11y/index.ts` (gate D2.3, its decision 0063) on 2026-10-20. Beside the
// settings feature (whose `Settings` model it reads, not imports) it gives a game:
//
//   - text size and contrast on every overlay outside the UI's own root: the UI's custom properties copied onto the
//     document's root, with `data-contrast` there for other overlays' rules;
//   - subtitles from the standard `dialogue:line` event (always on: dialogue is text): the speaker's name and the line,
//     held at least the minimum, in a `log` region; hidden while the game's own dialogue box shows the conversation;
//   - sound captions from the game's caption events ("[growl, left]"), a few at once, a repeat merged into the one
//     showing, shown while the captions setting is on (a `log` region too);
//   - the flash cap: the setting's number of flashes allowed to start in any second, handed to the game's flash system;
//   - pause anywhere on touch: a pause button while a context the touch overlay doesn't serve (dialogue, a cutscene)
//     hides it during play;
//   - a recap on load: after a slot loads, play opens paused under a "Previously" screen (the objective and the last
//     few things that happened, from a source the game passes).
//
// The numbers come from @rpg-unit/standard-settings' accessibility defaults; the settings read are the standard rows'
// (`accessibility.contrast`, `accessibility.flashes`, `accessibility.captions`). The seams are the standard
// `dialogue:*` events, a caption listener, the flow's state and ways out, a load listener and the input's active
// contexts, all passed in as ports; it imports no other feature.
import { Button, h, Menu, Screen, type ScreenRender, type Settings, type Translate } from "@rpg/ui";
import type { StandardEvents } from "@rpg/contracts";
import type { AccessibilityDefaults } from "@rpg-unit/standard-settings";

/** This unit's version (the manifest's and package.json's). */
export const ACCESSIBILITY_OPTIONS_VERSION = "0.1.0" as const;

/** The settings this feature reads (the standard rows'). */
export const A11Y_SETTINGS = { contrast: "accessibility.contrast", flashes: "accessibility.flashes", captions: "accessibility.captions" } as const;

/** What the recap lists: the current objective (text, or null) and the last few things that happened (text). */
export interface RecapSource {
  objective(): string | null;
  recent(n: number): string[];
}

/** A caption event (@rpg/audio's caption events fit): the caption's string id and where it came from. */
export interface CaptionLike {
  caption: string;
  direction?: string | null;
}

/** Planted defects, so the checks can prove they fail: `noSubtitleHold` drops the subtitle's minimum hold (a short
 *  line vanishes before it can be read); `captionsIgnoreSetting` shows captions with the setting off. */
export const A11Y_OPTION_FAULTS = { noSubtitleHold: false, captionsIgnoreSetting: false };

/** The recap screen, registered with the UI before accessibility mounts (its data is bound when it does). */
export function recapScreen(labels: { title?: string; objective?: string; noObjective?: string; continue?: string } = {}): {
  screens: Record<string, ScreenRender>;
  bind(source: RecapSource, close: () => void, events: number): void;
} {
  const l = { title: "recap.title", objective: "recap.objective", noObjective: "recap.noObjective", continue: "recap.continue", ...labels };
  let src: RecapSource | null = null;
  let close = (): void => undefined;
  let n = 3;
  return {
    screens: {
      recap: (ctx) => {
        const objective = src?.objective() ?? null;
        const recent = src?.recent(n) ?? [];
        return h(
          Screen,
          { ctx, title: ctx.t(l.title), dim: true, onBack: () => close() },
          h("p", { class: "rpg-recap-objective" }, objective ? ctx.t(l.objective, { objective }) : ctx.t(l.noObjective)),
          h("ul", { class: "rpg-recap-list" }, recent.map((line, i) => h("li", { key: i }, line))),
          h(Menu, null, h(Button, { label: ctx.t(l.continue), variant: "primary", autofocus: true, action: "recap-continue", onPress: () => close() })),
        );
      },
    },
    bind(s, c, events) {
      src = s;
      close = c;
      n = events;
    },
  };
}

/** The overlay's style (text scale, contrast and the theme's colours come from the UI's custom properties). */
export const A11Y_CSS = `
.rpg-a11y { position: fixed; inset: 0; z-index: 850; pointer-events: none; font-family: var(--rpg-font, system-ui, sans-serif); color: var(--rpg-text, #fbf6e8); }
.rpg-a11y-bottom { position: absolute; left: 50%; transform: translateX(-50%); bottom: max(96px, calc(env(safe-area-inset-bottom, 0px) + 96px)); width: min(760px, calc(100% - 32px)); display: flex; flex-direction: column; align-items: center; gap: 0.4em; font-size: calc(18px * var(--rpg-text-scale, 1)); line-height: 1.35; }
.rpg-a11y-captions { display: flex; flex-direction: column; align-items: center; gap: 0.25em; }
.rpg-a11y-captions[hidden] { display: none; }
.rpg-a11y-captions p, .rpg-a11y-subtitle p { margin: 0; padding: 0.25em 0.7em; border-radius: 8px; background: var(--rpg-panel, rgba(29, 42, 31, 0.9)); text-align: center; }
.rpg-a11y-captions p { color: var(--rpg-muted, #dcd5c0); font-style: italic; }
.rpg-a11y-subtitle .rpg-a11y-speaker { color: var(--rpg-accent, #e9c46a); font-weight: 650; margin-right: 0.5em; }
.rpg-a11y-subtitle .rpg-a11y-speaker::after { content: ":"; }
.rpg-a11y[data-boxed] .rpg-a11y-subtitle { display: none; }
:root[data-contrast] .rpg-a11y-captions p, :root[data-contrast] .rpg-a11y-subtitle p { background: #000; border: 2px solid var(--rpg-text, #fff); color: var(--rpg-text, #fff); font-style: normal; }
.rpg-a11y-pause { position: absolute; top: max(16px, env(safe-area-inset-top, 0px)); right: max(16px, env(safe-area-inset-right, 0px)); width: 48px; height: 48px; border-radius: 24px; border: 2px solid var(--rpg-text, #fff); background: var(--rpg-panel, rgba(0,0,0,0.6)); color: var(--rpg-text, #fff); pointer-events: auto; display: flex; align-items: center; justify-content: center; }
.rpg-a11y-pause[hidden] { display: none; }
.rpg-a11y-pause svg { width: 22px; height: 22px; }
`;

/** The standard events this feature reads and (for `say`) emits. */
type DialogueEvents = Pick<StandardEvents, "dialogue:line" | "dialogue:close">;

/** What accessibility needs from the game. */
export interface A11yOptionsDeps {
  /** The numbers (@rpg-unit/standard-settings' `accessibilityDefaults()` or the game's row). */
  defaults: AccessibilityDefaults;
  /** Strings: format, and whether an id exists. */
  t: Translate;
  has: (id: string) => boolean;
  /** The game's event bus, or an adapter to it (the standard dialogue events). */
  events: {
    on<K extends keyof DialogueEvents>(name: K, fn: (p: DialogueEvents[K]) => void): unknown;
    emit<K extends keyof DialogueEvents>(name: K, payload: DialogueEvents[K]): void;
  };
  /** The UI: its custom properties (text scale, theme) and a way to show the recap. */
  ui: { vars(): Record<string, string>; push(id: string, params?: unknown): void };
  /** The settings model (@rpg-unit/settings' or any @rpg/ui `Settings`). */
  settings: Pick<Settings, "get" | "subscribe">;
  /** The game's flow: its state, pause and resume, and its transitions. */
  flow: { state(): string | null; pause(): void; resume(): void; on(fn: (e: { to: string | null }) => void): unknown };
  /** A slot loaded (the recap follows). */
  onLoaded: (fn: (key: string) => void) => void;
  /** The input's active contexts, and the ones the touch overlay serves (default exploring, combat, system). */
  contexts: { active(): readonly string[] };
  touchContexts?: readonly string[];
  /** The game's caption events. */
  onCaption: (fn: (c: CaptionLike) => void) => void;
  /** Each frame, late, with the real (unscaled) delta (@rpg/runtime's `Runtime` fits). */
  runtime: { on(phase: "late", fn: (c: { realDt: number }) => void, o?: { name?: string; order?: number }): unknown };
  /** Hand the flash cap (flashes a second) to the game's flash system, and read it back. */
  setFlashCap: (perSecond: number) => void;
  flashCap: () => number;
  /** The recap's data and its screen (registered with the UI). */
  recap: ReturnType<typeof recapScreen>;
  recapSource: RecapSource;
  /** A speaker's display name (a string id) where the game knows one, else null. */
  speakerName?: (speaker: string) => string | null;
  /** Whether the game's dialogue box shows the conversation now (its lines are on screen there already). */
  boxOpen?: () => boolean;
  /** Whether touch is the device in use. */
  touch: () => boolean;
  /** Talk with someone as a press on them would (for the handle), or null where the game has no one to talk to. */
  talk?: ((npc: string) => void) | null;
  /** The document to draw in (default `document`). */
  doc?: Document;
  /** String ids (spike 002's by default). */
  labels?: { overlay?: string; captions?: string; subtitles?: string; pause?: string; villager?: string; captionLine?: string; captionLineDir?: string };
}

/** Mount the overlay and the options; returns the handle's keys. */
export function mountAccessibilityOptions(d: A11yOptionsDeps) {
  const row = d.defaults;
  const t = d.t;
  const doc = d.doc ?? document;
  const l = { overlay: "a11y.overlay", captions: "a11y.captions", subtitles: "a11y.subtitles", pause: "action.pause", villager: "character.villager", captionLine: "caption.line", captionLineDir: "caption.lineDir", ...d.labels };
  const touchContexts = new Set(d.touchContexts ?? ["exploring", "combat", "system"]);

  // the overlay: captions over the subtitle, at the bottom; the touch pause button at the top right
  if (!doc.getElementById("rpg-a11y-style")) {
    const style = doc.createElement("style");
    style.id = "rpg-a11y-style";
    style.textContent = A11Y_CSS;
    doc.head.appendChild(style);
  }
  const root = doc.createElement("div");
  root.className = "rpg-a11y";
  root.dataset.a11y = "";
  // a named region is a landmark: the subtitles and captions are page content a screen reader can find
  root.setAttribute("role", "region");
  root.setAttribute("aria-label", t(l.overlay));
  const bottom = doc.createElement("div");
  bottom.className = "rpg-a11y-bottom";
  const captions = doc.createElement("div");
  captions.className = "rpg-a11y-captions";
  captions.setAttribute("role", "log");
  captions.setAttribute("aria-live", "polite");
  captions.setAttribute("aria-label", t(l.captions));
  const subtitle = doc.createElement("div");
  subtitle.className = "rpg-a11y-subtitle";
  subtitle.setAttribute("role", "log");
  subtitle.setAttribute("aria-live", "polite");
  subtitle.setAttribute("aria-label", t(l.subtitles));
  const line = doc.createElement("p");
  line.hidden = true;
  const speaker = doc.createElement("span");
  speaker.className = "rpg-a11y-speaker";
  const text = doc.createElement("span");
  text.className = "rpg-a11y-text";
  line.append(speaker, text);
  subtitle.appendChild(line);
  bottom.append(captions, subtitle);
  const pause = doc.createElement("button");
  pause.className = "rpg-a11y-pause";
  pause.type = "button";
  pause.hidden = true;
  pause.dataset.action = "pause-anywhere";
  pause.setAttribute("aria-label", t(l.pause));
  pause.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor"/><rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor"/></svg>';
  pause.addEventListener("click", () => d.flow.pause());
  root.append(bottom, pause);
  doc.body.appendChild(root);

  // text size and contrast on the document's root, for every overlay outside the UI's own
  const applyVars = (): void => {
    const rootEl = doc.documentElement;
    for (const [k, v] of Object.entries(d.ui.vars())) rootEl.style.setProperty(k, v);
    rootEl.toggleAttribute("data-contrast", d.settings.get<string>(A11Y_SETTINGS.contrast) === "high");
    d.setFlashCap(d.settings.get<string>(A11Y_SETTINGS.flashes) === "reduced" ? row.flashCap.reduced : row.flashCap.standard);
    captions.hidden = !A11Y_OPTION_FAULTS.captionsIgnoreSetting && d.settings.get<boolean>(A11Y_SETTINGS.captions) !== true;
  };
  d.settings.subscribe(applyVars);
  applyVars();

  // subtitles: one line at a time, held at least the minimum
  const sub = { speaker: null as string | null, text: "", until: 0, shown: 0 };
  let clock = 0;
  const showLine = (who: string, words: string, seconds: number): void => {
    // one name per speaker: the game's (as its dialogue box says it), else the strings' `character.<id>`, else a villager
    const known = d.speakerName?.(who) ?? null;
    const name = known && d.has(known) ? t(known) : d.has(`character.${who}`) ? t(`character.${who}`) : t(l.villager);
    speaker.textContent = name;
    text.textContent = words;
    line.hidden = false;
    sub.speaker = who;
    sub.text = words;
    sub.until = clock + (A11Y_OPTION_FAULTS.noSubtitleHold ? seconds : Math.max(seconds, row.subtitleMinSeconds));
    sub.shown++;
  };
  d.events.on("dialogue:line", (x) => showLine(x.speaker, x.text, x.seconds));
  d.events.on("dialogue:close", () => void (sub.until = Math.min(sub.until, clock + 0.6)));

  // captions: a few at once, repeats within the merge window refresh the one showing
  const live: { key: string; el: HTMLParagraphElement; until: number }[] = [];
  const shownLog: string[] = [];
  d.onCaption((c) => {
    const sound = t(c.caption);
    const words = c.direction ? t(l.captionLineDir, { sound, dir: t(`caption.dir.${c.direction}`) }) : t(l.captionLine, { sound });
    const key = words;
    const same = live.find((x) => x.key === key && x.until - row.captionSeconds + row.captionMerge >= clock);
    if (same) {
      same.until = clock + row.captionSeconds;
      return;
    }
    const el = doc.createElement("p");
    el.textContent = words;
    captions.appendChild(el);
    live.push({ key, el, until: clock + row.captionSeconds });
    while (live.length > row.captionMax) live.shift()!.el.remove();
    shownLog.push(words);
    if (shownLog.length > 50) shownLog.shift();
  });

  // the clock, expiry, the dialogue box's copy, and pause anywhere on touch
  d.runtime.on(
    "late",
    (c) => {
      clock += c.realDt;
      if (!line.hidden && clock >= sub.until) line.hidden = true;
      // the dialogue box shows a conversation's lines itself: no second copy under it
      const boxed = d.boxOpen?.() === true;
      if (boxed !== root.hasAttribute("data-boxed")) root.toggleAttribute("data-boxed", boxed);
      for (let i = live.length - 1; i >= 0; i--) if (clock >= live[i]!.until) live.splice(i, 1)[0]!.el.remove();
      // the touch overlay hides while a context it doesn't serve (dialogue, a cutscene) is up: keep a way to pause
      const blocked = d.contexts.active().some((x) => !touchContexts.has(x));
      const want = d.flow.state() === "play" && d.touch() && blocked;
      if (pause.hidden === want) pause.hidden = !want;
    },
    { name: "accessibility: subtitles, captions, pause button", order: 60 },
  );

  // the recap on load: a slot loaded, play opens paused under the recap
  let pending = false;
  let recaps = 0;
  const close = (): void => d.flow.resume();
  d.recap.bind(d.recapSource, close, row.recapEvents);
  d.onLoaded(() => void (pending = true));
  d.flow.on((e) => {
    if (e.to !== "play" || !pending) return;
    pending = false;
    queueMicrotask(() => {
      if (d.flow.state() !== "play") return;
      d.flow.pause();
      d.ui.push("recap");
      recaps++;
    });
  });

  return {
    handle: {
      /** What shows now: the subtitle, the captions, the touch pause button; the settings in force. */
      state: () => ({
        subtitle: line.hidden ? null : { speaker: sub.speaker, name: speaker.textContent, text: sub.text },
        subtitlesShown: sub.shown,
        captions: live.map((x) => x.el.textContent),
        captionsShown: [...shownLog],
        captionsVisible: !captions.hidden,
        pauseButton: !pause.hidden,
        contrast: doc.documentElement.hasAttribute("data-contrast") ? "high" : "normal",
        textScale: (typeof getComputedStyle === "function" ? Number(getComputedStyle(doc.documentElement).getPropertyValue("--rpg-text-scale")) : 0) || 1,
        flashCap: d.flashCap(),
        recaps,
      }),
      /** Say a line on the event bus, as a story's runner says one (`dialogue:line`: the subtitle, the babble). */
      say: (who: string, stringId: string, seconds = 4) => {
        d.events.emit("dialogue:line", { speaker: who, stringId, text: t(stringId), seconds });
      },
      /** Talk with someone through the game's story (its dialogue box opens); a game with no one to talk to throws. */
      talk: (npc: string) => {
        if (!d.talk) throw new Error("accessibility: no story in this page to talk through");
        d.talk(npc);
      },
      /** The recap's contents now. */
      recap: () => ({ objective: d.recapSource.objective(), recent: d.recapSource.recent(row.recapEvents) }),
    },
  };
}
